/* istanbul ignore file */

import type { ProductListResult, ProductStatus } from '@partful/shared-types';

interface FilteredProducts {
  completedProducts: ProductListResult[];
  processingProducts: ProductListResult[];
  failedProcessingProducts: ProductListResult[];
  inProgressProducts: ProductListResult[]; // when awaiting for user input to continue
}

export function filterProductsByProcessingState(products: ProductListResult[]): FilteredProducts {
  const filteredProducts: FilteredProducts = {
    completedProducts: [],
    processingProducts: [],
    failedProcessingProducts: [],
    inProgressProducts: [],
  };

  products.forEach((product) => {
    const { status } = product;
    /* istanbul ignore next */ if (status === undefined) return; // old-gen products, shouldnt get here but is in type...

    if (isProcessing(status)) {
      filteredProducts.processingProducts.push(product);
    } else if (isProcessingFailed(status)) {
      filteredProducts.failedProcessingProducts.push(product);
    } else if (isInProgress(status)) {
      filteredProducts.inProgressProducts.push(product);
    } else if (isComplete(status)) {
      filteredProducts.completedProducts.push(product);
    }
  });

  return filteredProducts;
}

/* istanbul ignore next */
export function filterUncategorizedProducts(products: ProductListResult[]): ProductListResult[] {
  return products.filter((product) => !product.productCategories || product.productCategories.length === 0);
}

const isProcessing = (status: ProductStatus): boolean => {
  if (status.cadJob) {
    return status.cadJob === 'IN_PROGRESS_PROCESSING_CAD';
  }

  return false; // it is not processing
};

const isProcessingFailed = (status: ProductStatus): boolean => {
  if (status.cadJob) {
    return status.cadJob.endsWith('FAILED');
  }

  if (status.partCategoriesJob) {
    return status.partCategoriesJob.endsWith('FAILED');
  }

  return false; // processing has not failed
};

const isInProgress = (status: ProductStatus): boolean => {
  if (status.cadJob) {
    return status.cadJob.startsWith('IN_PROGRESS_') && !status.cadJob.includes('PROCESSING');
  }

  if (status.partCategoriesJob) {
    return status.partCategoriesJob.startsWith('IN_PROGRESS_');
  }

  return false; // it is not inProgress
};

const isComplete = (status: ProductStatus): boolean => status.partTree === 'COMPLETED';

export const urlProgressMap = ({
  status,
  runID,
  name,
  configurationPID,
  productExternalID,
}: Partial<ProductListResult>): string => {
  if (status?.cadJob) {
    /* istanbul ignore next */ switch (status.cadJob) {
      case 'IN_PROGRESS_REQUIRES_CAD_FILE':
        // No need to redirect with the product name for this one as the user could change the name
        return `/create-product/cad?runID=${runID}`;

      case 'IN_PROGRESS_REQUIRES_GENERAL_ASSEMBLY':
        // ConfigurationPID is only set after CAD processing is complete
        return `/create-product/cad/general-assembly?runID=${runID}&productName=${name}`;

      case 'IN_PROGRESS_PROCESSING_CAD':
      case 'IN_PROGRESS_CAD_PROCESSING_FAILED':
      case 'FAILED':
        // Failed state should be filtered out by API
        return `/create-product/cad/processing?runID=${runID}&productName=${name}`;

      case 'IN_PROGRESS_REQUIRES_ORIENTATION_MAPPING':
      case 'IN_PROGRESS_REQUIRES_ATTRIBUTE_MAPPING':
        if (configurationPID != null) {
          return `/create-product/cad/select-part-number?runID=${runID}&configurationPID=${configurationPID}&productName=${name}`;
        }
        /* istanbul ignore next */
        break;

      case 'IN_PROGRESS_REQUIRES_SALES_BOM':
        if (configurationPID != null) {
          return `/create-product/sales-bom?configurationPID=${configurationPID}&productName=${name}&runID=${runID}`;
        }
        /* istanbul ignore next */
        break;
      /* istanbul ignore next */
      // No complete state as this should be filtered out by products page
      default:
        return '/product-library?error=CouldntMapProduct';
    }
  }

  /* istanbul ignore next */ if (status?.partCategoriesJob) {
    if (productExternalID != null && configurationPID != null && runID != null) {
      return `/manage-product/${productExternalID}/parts-categories?configurationPID=${configurationPID}&runID=${runID}`;
    }
  }

  /* istanbul ignore next */
  // Fallback return statement
  return '/product-library?error=CouldntMapProduct';
};
